import type { LoaderFunctionArgs } from "react-router";
import { useLoaderData } from "react-router";
import { buildClientSchema } from "graphql";
import Tournaments from "~/components/tournaments/Tournaments";
import { sendGraphQLRequest } from "~/utils/graphql.server";
import schema from "~/@graphql/schema.server.json";
import getGQLFile from "~/utils/get-gql-file.server";
import { prepareManyTournamentsData } from "~/helpers/prepare-tournament-data";
import { getSession } from "~/session.server";
import type { GraphQLError, IntrospectionQuery } from "graphql";
import type { GetTournamentsQuery } from "~/@graphql/types";
import type { TournamentProps } from "~/helpers/prepare-tournament-data";
import TournamentsCalendar from "~/components/tournaments/Calendar";
import { countDaysBetween, groupByKey } from "~/utils/belt";
import FeaturedTournaments from "~/components/tournaments/FeaturedTournaments";

const processTournamentData = ({
  data,
  searchTerm,
}: {
  data?: GetTournamentsQuery;
  searchTerm: string;
}) => {
  return prepareManyTournamentsData(data!).filter((tournament) => {
    if (searchTerm) {
      const evaluation =
        tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tournament.tournamentType
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        tournament.game?.toLowerCase().includes(searchTerm.toLowerCase());
      return evaluation;
    }
    return true;
  });
};

type LoaderData = { data?: GetTournamentsQuery; errors?: GraphQLError[] };

export const loader = async (args: LoaderFunctionArgs) => {
  const session = await getSession(args.request, false);
  const token = session.get("accessToken");
  let query = getGQLFile("queries/get-tournaments");
  const authorization: string | undefined = `Bearer ${token}`;
  let headers:
    | {
      authorization: string;
    }
    | undefined = { authorization };
  if (!token) {
    query = getGQLFile("queries/get-tournaments-anon", ["tournaments"]);
    headers = undefined;
  }

  const response = await sendGraphQLRequest({
    args,
    endpoint: process.env.GRAPHQL_API!,
    headers,
    schema: buildClientSchema(schema as unknown as IntrospectionQuery),
    query,
    variables: { limit: 6, offset: 0 },
  });

  if (response.ok) {
    let result = await response.json();
    return result
  }
  return { errors: [{ message: response.statusText }] }
};

export function meta() {
  return [{
    title: "Tournaments — Jiwe eSports",
  }]
}

export default function TournamentsIndex() {
  const { data, errors } = useLoaderData() as unknown as LoaderData;

  if (errors) {
    return (
      <>
        {errors.map((err: any) => (
          <>{err.message}</>
        ))}
      </>
    ); // TODO: Handle errors better
  }

  const tournaments = (processTournamentData({ data, searchTerm: "" }) || []).sort((a, b) =>
    new Date(b.dates.tournamentDates.start).getTime() - new Date(a.dates.tournamentDates.start).getTime()
  );
  let showMeta = false;

  if (typeof window !== "undefined") {
    showMeta = window.innerWidth < 1024;
  }

  const tournamentsWithDates: TournamentProps[] = tournaments
    .map((trn) => {
      const map: TournamentProps[] = [];
      const daysBetween = countDaysBetween(
        trn.dates.tournamentDates.end,
        trn.dates.tournamentDates.start
      );
      let date = trn.dates.tournamentDates.start;
      date.setDate(date.getDate() - 1);
      // @ts-expect-error
      map.push({ ...trn, _date: date.toISOString().split("T")[0] });
      date = trn.dates.tournamentDates.start;
      for (let i = 0; i < daysBetween; i++) {
        date.setDate(date.getDate() + 1);
        // @ts-expect-error
        map.push({ ...trn, _date: date.toISOString().split("T")[0] });
      }
      return map;
    })
    .reduce((acc, val) => [...acc, ...val], []);

  const daysTournaments: { [key: string]: TournamentProps[] } = groupByKey(
    tournamentsWithDates,
    "_date"
  );

  const tournamentDates = Object.keys(
    groupByKey(
      tournaments
        .map((trn) => {
          const map: TournamentProps[] = [];
          const daysBetween = countDaysBetween(
            trn.dates.tournamentDates.end,
            trn.dates.tournamentDates.start
          );
          let date = trn.dates.tournamentDates.start;
          if (daysBetween === 0) {
            // @ts-expect-error
            map.push({ _date: date.toISOString().split("T")[0] });
          }
          for (let i = 0; i < daysBetween; i++) {
            date.setDate(date.getDate() + 1);
            // @ts-expect-error
            map.push({ _date: date.toISOString().split("T")[0] });
          }
          return map;
        })
        .reduce((acc, val) => [...acc, ...val], []),
      "_date"
    )
  ).map((d) => new Date(d));

  return (
    <div className="min-h-[50vh] pt-24 px-5 lg:pt-0">
      <div
        className="relative -ml-5 py-6 bg-jiwe-dark-blue-translucent lg:bg-[transparent]"
        style={{
          width: "calc(100% + 2.5rem)",
        }}
      >
        <div className="slanting absolute left-0 border-t-jiwe-dark-blue-translucent w-full -top-4 border-t-[1rem] border-r-[transparent] border-r-[100vw] rotate-180 lg:hidden" />
        <h1 className="uppercase text-jiwe-dark-blue lg:text-[#000] dark:lg:text-grey-light dark:text-[#FDFDFD] font-black lg:font-bold text-3xl text-center lg:text-left lg:text-6xl -mt-3">
          Tournaments
        </h1>
        <div className="slanting absolute left-0 border-t-jiwe-dark-blue w-full bottom-0 border-t-[1rem] border-r-[transparent] border-r-[100vw] rotate-180 lg:hidden" />
      </div>
      <Tournaments showMeta={showMeta} tournaments={tournaments} />
      <div className="w-14 my-12 border-t-[.5px] border-t-divider dark:border-t-divider-dark" />
      <TournamentsCalendar
        tournaments={tournaments}
        daysTournaments={daysTournaments}
        tournamentDates={tournamentDates}
      />
      <div className="w-14 my-12 border-t-[.5px] border-t-divider dark:border-t-divider-dark" />
      <FeaturedTournaments tournaments={tournaments} />
      <div className="w-14 my-12 border-t-[.5px] border-t-divider dark:border-t-divider-dark" />
      <div className="max-w-xs mb-32">
        <h3 className="text-grey-night dark:text-grey-border uppercase font-bold text-2xl min-w-[30%]">
          Looking to sponsor a tournament?
        </h3>
        <a
          href="mailto:rock@jiwe.io"
          className="hover:opacity-90 my-4 py-3 px-8 block w-fit text-jiwe-dark-blue bg-jiwe-yellow uppercase font-bold text-sm rounded-3xl"
        >
          Get in touch
        </a>
      </div>
    </div>
  );
}
